import styled from 'styled-components';
import { useContext } from 'react';

import Typography from '../../../kingpin/atoms/Typography';
import Row from '../../Common/Row';
import Icon from '../../../kingpin/atoms/Icon';
import useGetFieldLabel from '../../../hooks/useGetFieldLabel';
import useValueFormatters from '../../../hooks/useValueFormatters';
import { BonusOverviewContext } from '../providers/BonusOverviewProvider';
import { isArnoldBrosRewardBands } from '../utils/rewardBandsUtils';

// The dark tooltip shell — matches AggregatedRewardTooltip's Outer so a single band section reads
// like a slice of the full reward tooltip when shown on its own (the sub-reward row's info icon).
const Outer = styled.div`
  padding: 16px;
  min-width: 280px;
  background-color: var(--color-tooltip-bg);
  border-radius: 8px;
`;

const Section = styled.div`
  background-color: var(--color-tooltip-section-bg);
  padding: 16px;
  border-radius: 8px;
`;

const SectionTitleRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--color-tooltip-divider);
`;

const SectionRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BandRow = styled.div<{ isActive: boolean; isAchieved: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 4px 8px;
  margin: 0 -8px;
  border-radius: 8px;
  background-color: ${({ isActive }) =>
    isActive ? 'var(--color-tooltip-row-active-bg)' : 'transparent'};
  opacity: ${({ isActive, isAchieved }) =>
    isActive ? '1' : isAchieved ? '0.35' : '1'};
`;

/**
 * The band ladder for a single banded sub-reward (e.g. Veriha fuel's mpg / idle): the driver's
 * value against each paying tier, with the tier they landed in marked, plus an always-shown fail
 * band (the $0 threshold, expressed with a strict > / < complement of the tiers) so the driver
 * sees where the fail region starts even once a target is reached. Shared between
 * AggregatedRewardTooltip (the full reward tooltip lists one per component) and the inline
 * sub-reward row's info tooltip, so both surfaces render the identical section.
 */
export const SubRewardBandSection = ({
  sub,
}: {
  sub: BonusTypes.Rewards.AnyRewardOutput;
}) => {
  const { bonusOverview } = useContext(BonusOverviewContext);
  const { getFieldLabel } = useGetFieldLabel();
  const { formatField, formatValue } = useValueFormatters();

  if (!bonusOverview) {
    return null;
  }

  const bands = sub.rewardBands;
  if (!bands || isArnoldBrosRewardBands(bands)) {
    return null;
  }

  const { dataset } = bonusOverview.bonusProgram;

  const formatMoney = (value: number): string =>
    formatValue({
      value,
      formatting: { prefix: 'currency', precision: 2 },
    }).toString();

  // Describe a paying tier's threshold with the same operator convention as the other band
  // tooltips (RewardTierTooltip): ≥ / ≤ / = rather than "At least" / "At most" / "Exactly".
  const explainBand = (band: BonusTypes.Rewards.RewardBand): string => {
    const value = band.min ?? band.max ?? band.equal;
    if (value === undefined) {
      return '';
    }
    const operator = band.min != null ? '≥' : band.max != null ? '≤' : '=';
    return `${operator} ${formatField({ field: bands.field, dataset, value })}`;
  };

  // Classify each tier for its row styling, matching RewardBandsTooltip (the canonical band
  // tooltip this section mirrors) exactly: the `achieved` tiers — those the driver has NOT reached
  // (a higher min they fall short of, or a stricter max they exceed) — are faded to 0.35; tiers
  // they've cleared stay full opacity and the tier they landed in is highlighted.
  const getBandMode = (
    band: BonusTypes.Rewards.RewardBand,
  ): 'active' | 'achieved' | 'unmet' => {
    if (band.active) {
      return 'active';
    }
    if (band.min != null) {
      return bands.fieldValue < band.min ? 'achieved' : 'unmet';
    }
    if (band.max != null) {
      return bands.fieldValue > band.max ? 'achieved' : 'unmet';
    }
    if (band.equal != null) {
      return bands.fieldValue === band.equal ? 'achieved' : 'unmet';
    }
    return 'unmet';
  };

  const fieldLabel = getFieldLabel({ field: bands.field, dataType: dataset });
  const actualValue = formatField({
    field: bands.field,
    dataset,
    value: bands.fieldValue,
  });

  // The paying tiers (reward > 0). Any explicit $0 band in the data is the fail band — surfaced by
  // the synthesised fail row below rather than listed as a tier, so it isn't shown twice.
  const payingBands = bands.bands.filter((band) => (band.reward ?? 0) > 0);
  // The fail band is active when the driver cleared no paying tier.
  const failActive = !payingBands.some((band) => band.active);

  // The failing threshold = the strict complement of the easiest qualifying boundary, so the fail
  // band is always visible (even once a target is reached) and reads unambiguously against the
  // tiers' ≥ / ≤ — e.g. paying ≥ 8 mpg ⇒ fail < 8; paying ≤ 6% idle ⇒ fail > 6%.
  const failBand = ((): { operator: '>' | '<'; value: number } | undefined => {
    const mins = payingBands
      .map((band) => band.min)
      .filter((value): value is number => value != null);
    if (mins.length > 0) {
      return { operator: '<', value: Math.min(...mins) };
    }
    const maxes = payingBands
      .map((band) => band.max)
      .filter((value): value is number => value != null);
    if (maxes.length > 0) {
      return { operator: '>', value: Math.max(...maxes) };
    }
    return undefined;
  })();
  const failLabel = failBand
    ? `${failBand.operator} ${formatField({ field: bands.field, dataset, value: failBand.value })}`
    : undefined;

  const renderBandRow = ({
    key,
    label,
    reward,
    isActive,
    isAchieved,
  }: {
    key: string;
    label: string;
    reward: number;
    isActive: boolean;
    isAchieved: boolean;
  }) => {
    const color = isActive
      ? 'var(--color-tooltip-text)'
      : 'var(--color-tooltip-text-muted)';
    const bodyType = isActive ? 'Body 13 Bold' : 'Body 13';
    return (
      <BandRow key={key} isActive={isActive} isAchieved={isAchieved}>
        <Row centerAlign style={{ gap: 4 }}>
          <Icon icon={'circleCheckmark'} color={color} />
          <Typography.Body type={bodyType} color={color}>
            {label}
          </Typography.Body>
        </Row>
        <Typography.Body type={bodyType} color={color}>
          {formatMoney(reward)}
        </Typography.Body>
      </BandRow>
    );
  };

  return (
    <Section>
      <SectionTitleRow>
        <Typography.Body
          type={'Body 14 Bold'}
          color={'var(--color-tooltip-text)'}
        >
          {fieldLabel}
        </Typography.Body>
        <Typography.Body type={'Body 14'} color={'var(--color-tooltip-text)'}>
          {actualValue}
        </Typography.Body>
      </SectionTitleRow>
      <SectionRows>
        {failLabel &&
          renderBandRow({
            key: 'fail',
            label: failLabel,
            reward: 0,
            isActive: failActive,
            isAchieved: false,
          })}
        {payingBands.map((band) => {
          const mode = getBandMode(band);
          return renderBandRow({
            key: `${bands.field}-${band.min ?? ''}-${band.max ?? ''}-${band.equal ?? ''}`,
            label: explainBand(band),
            reward: band.reward ?? 0,
            isActive: mode === 'active',
            isAchieved: mode === 'achieved',
          });
        })}
      </SectionRows>
    </Section>
  );
};

/**
 * Standalone tooltip wrapper: a single band section inside the dark tooltip shell — used as the
 * content for a sub-reward row's info icon (the full reward tooltip embeds SubRewardBandSection
 * directly instead, alongside its other sections).
 */
export const SubRewardBandTooltip = ({
  sub,
}: {
  sub: BonusTypes.Rewards.AnyRewardOutput;
}) => (
  <Outer>
    <SubRewardBandSection sub={sub} />
  </Outer>
);

export default SubRewardBandSection;
